import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import {
  validateInstructorInviteToken,
  acceptInstructorInvite,
} from "../api/apiClient";

// ── Design tokens (Accept Invitation.html) ────────────────────────────────────
const DS = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-surface-press-light)",
  surface2: "var(--color-surface-2)",
  pageBg: "#F0EEE9",
  danger: "#B42318",
};

const STRENGTH_COLORS = ["#B42318", "#B5651D", "#B5651D", "#15803D"];
const STRENGTH_LABELS = ["Too short", "Weak", "Good", "Strong"];

function scorePassword(v) {
  if (!v) return -1;
  if (v.length < 8) return 0;
  let score = 0;
  if (v.length >= 8) score++;
  if (v.length >= 12) score++;
  if (/[0-9]/.test(v) && /[a-zA-Z]/.test(v)) score++;
  if (/[^a-zA-Z0-9]/.test(v)) score++;
  return Math.min(score, 4);
}

const ACCESS_ITEMS = [
  "Build and run skill check sheets for your classes",
  "Take live attendance and manage class rosters",
  "Create and grade written tests",
  "Track your own teaching certifications",
];

function LockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function BadgeIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4a5a12" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5-10-5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  );
}

function CheckIcon({ color }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function FieldLabel({ children, required, hint }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "12.5px", fontWeight: 700, color: DS.ink2, mb: "7px" }}>
      <span>{children}{required && <Box component="span" sx={{ color: DS.danger, ml: "2px" }}>*</Box>}</span>
      {hint && <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 600, color: DS.ink4 }}>{hint}</Box>}
    </Box>
  );
}

function inputSx(primary, primary10, extra) {
  return {
    width: "100%", height: 46, bgcolor: "#fff", border: `1.5px solid ${DS.line}`, borderRadius: "8px",
    px: "14px", fontSize: "14.5px", color: DS.ink, fontFamily: "inherit", outline: "none",
    transition: "border-color .15s, box-shadow .15s, background .15s",
    "&:focus": { borderColor: primary, boxShadow: `0 0 0 4px ${primary10}` },
    "&::placeholder": { color: DS.ink4 },
    ...extra,
  };
}

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary700 = theme.palette.primary.dark;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tSecondary = theme.palette.secondary.main;

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);

  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [created, setCreated] = useState(false);
  const [mergedExistingAccount, setMergedExistingAccount] = useState(false);

  useEffect(() => {
    validateInstructorInviteToken(token)
      .then((data) => setInvite(data))
      .catch((err) => {
        const status = err?.response?.status;
        const msg =
          status === 410
            ? "This invitation has already been used or has expired."
            : status === 404
            ? "Invitation not found. The link may be invalid."
            : "Could not load invitation. Please try again.";
        setLoadError(msg);
      })
      .finally(() => setLoadingInvite(false));
  }, [token]);

  const score = scorePassword(password);
  const matchState = !confirmPassword ? null : confirmPassword === password ? "ok" : "bad";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) return;
    if (password.length < 8) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await acceptInstructorInvite(token, username.trim(), password, fullName.trim() || undefined);
      setCreated(true);
      setMergedExistingAccount(Boolean(result?.mergedExistingAccount));
      setTimeout(() => navigate("/login?invited=1"), result?.mergedExistingAccount ? 1800 : 900);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to create account. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInvite) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: DS.pageBg }}>
        <CircularProgress sx={{ color: tPrimary }} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: DS.pageBg, px: 2 }}>
        <Box sx={{ width: "100%", maxWidth: 460 }}>
          <Alert severity="error" sx={{ borderRadius: "12px" }}>{loadError}</Alert>
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Button variant="text" onClick={() => navigate("/login")} sx={{ textTransform: "none", fontWeight: 700, color: tPrimary }}>
              Go to Login
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  const canSubmit =
    !submitting && !created && fullName.trim() && username.trim().length >= 3 &&
    password.length >= 8 && confirmPassword === password && agree;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "var(--color-surface-2)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        py: 6, px: 2.5,
      }}
    >
      <Box
        sx={{
          width: "100%", maxWidth: 460, bgcolor: "#fff", borderRadius: "18px",
          boxShadow: "0 30px 70px -28px rgba(21,15,35,.22), 0 10px 24px -14px rgba(21,15,35,.14)",
          border: `1px solid ${DS.line}`, overflow: "hidden",
        }}
      >
        <Box sx={{ p: { xs: "28px 22px 24px", sm: "36px 36px 30px" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "24px" }}>
            <Box sx={{ width: 34, height: 34, borderRadius: "8px", bgcolor: tPrimary, display: "grid", placeItems: "center", flexShrink: 0 }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            </Box>
            <Box>
              <Typography sx={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 15, color: DS.ink, letterSpacing: "-.01em" }}>
                Training Center
              </Typography>
              <Typography sx={{ fontSize: 12, color: DS.ink, opacity: 0.55, mt: "1px" }}>{invite?.tenantName}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#4a5a12", bgcolor: "rgba(194,239,78,.35)", py: "6px", pr: "12px", pl: "9px", borderRadius: "999px", mb: "18px" }}>
            <BadgeIcon /> Instructor invitation
          </Box>

          <Typography sx={{ fontSize: 24, fontWeight: 600, letterSpacing: "-.01em", color: DS.ink, mb: "10px", fontFamily: "var(--font-display)" }}>
            Accept your invitation
          </Typography>
          <Typography sx={{ fontSize: "14.5px", color: DS.ink2, lineHeight: 1.6, mb: "28px" }}>
            You've been invited to join <Box component="strong" sx={{ color: DS.ink, fontWeight: 700 }}>{invite?.tenantName}</Box> as{" "}
            <Box component="span" sx={{ fontWeight: 700, color: tPrimary }}>an instructor</Box>. Create your account below to get started.
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}
          {created && mergedExistingAccount && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
              This email already had an account — the instructor role was added to it and the name
              was updated. Sign in with that account's existing username and password, not the ones
              entered above.
            </Alert>
          )}
          {created && !mergedExistingAccount && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: "10px" }}>
              Account created — signing you in…
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ mb: "16px" }}>
              <FieldLabel>Email</FieldLabel>
              <Box sx={{ position: "relative" }}>
                <Box component="input" value={invite?.email || ""} readOnly sx={inputSx(tPrimary, tPrimary10, { pr: "44px", bgcolor: DS.surface2, color: DS.ink2, cursor: "not-allowed" })} />
                <Box sx={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: DS.ink4, pointerEvents: "none" }}>
                  <LockIcon />
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: "16px" }}>
              <FieldLabel required>Full Name</FieldLabel>
              <Box component="input" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Jordan Beyer" required sx={inputSx(tPrimary, tPrimary10)} />
            </Box>

            <Box sx={{ mb: "16px" }}>
              <FieldLabel required hint="Min. 3 characters">Username</FieldLabel>
              <Box component="input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Choose a username" required minLength={3} sx={inputSx(tPrimary, tPrimary10)} />
            </Box>

            <Box sx={{ mb: "16px" }}>
              <FieldLabel required hint="Min. 8 characters">Password</FieldLabel>
              <Box sx={{ position: "relative" }}>
                <Box
                  component="input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                  sx={inputSx(tPrimary, tPrimary10, { pr: "44px" })}
                />
                <IconButton
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  sx={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "6px", color: DS.ink3, "&:hover": { bgcolor: "var(--color-surface-press-light)", color: DS.ink } }}
                >
                  <EyeIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", gap: "4px", mt: "8px" }}>
                {[0, 1, 2, 3].map((i) => (
                  <Box key={i} sx={{ height: 4, flex: 1, borderRadius: "3px", bgcolor: password && i < Math.max(score, 1) ? STRENGTH_COLORS[Math.max(score - 1, 0)] : DS.line2, transition: "background .2s" }} />
                ))}
              </Box>
              <Box sx={{ fontSize: "11.5px", mt: "6px", minHeight: "15px", color: password ? STRENGTH_COLORS[Math.max(score - 1, 0)] : DS.ink3 }}>
                {password ? STRENGTH_LABELS[Math.max(score - 1, 0)] : ""}
              </Box>
            </Box>

            <Box sx={{ mb: "16px" }}>
              <FieldLabel required>Confirm Password</FieldLabel>
              <Box sx={{ position: "relative" }}>
                <Box
                  component="input"
                  type={showConfirmPw ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  sx={inputSx(tPrimary, tPrimary10, { pr: "44px" })}
                />
                <IconButton
                  type="button"
                  onClick={() => setShowConfirmPw((s) => !s)}
                  aria-label={showConfirmPw ? "Hide password" : "Show password"}
                  sx={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "6px", color: DS.ink3, "&:hover": { bgcolor: "var(--color-surface-press-light)", color: DS.ink } }}
                >
                  <EyeIcon />
                </IconButton>
              </Box>
              <Box sx={{ fontSize: "11.5px", mt: "6px", minHeight: "15px", color: matchState === "ok" ? tSecondary : matchState === "bad" ? DS.danger : DS.ink3 }}>
                {matchState === "ok" ? "Passwords match" : matchState === "bad" ? "Passwords do not match" : ""}
              </Box>
            </Box>

            <Box sx={{ bgcolor: DS.surface2, border: `1px solid ${DS.line2}`, borderRadius: "10px", p: "16px 18px", my: "22px" }}>
              <Typography sx={{ fontSize: "11.5px", fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: DS.ink3, mb: "11px" }}>
                What you'll be able to do
              </Typography>
              <Box component="ul" sx={{ display: "flex", flexDirection: "column", gap: "9px", listStyle: "none", m: 0, p: 0 }}>
                {ACCESS_ITEMS.map((item) => (
                  <Box component="li" key={item} sx={{ display: "flex", alignItems: "flex-start", gap: "9px", fontSize: "13.5px", color: DS.ink2, fontWeight: 500, lineHeight: 1.4 }}>
                    <Box sx={{ flexShrink: 0, mt: "2px" }}><CheckIcon color={tPrimary} /></Box>
                    <span>{item}</span>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", mb: "22px" }}>
              <Box
                component="input"
                type="checkbox"
                id="agree"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                sx={{ width: 18, height: 18, mt: "1px", accentColor: tPrimary, flexShrink: 0, cursor: "pointer" }}
              />
              <Box component="label" htmlFor="agree" sx={{ fontSize: 13, color: DS.ink2, lineHeight: 1.5, cursor: "pointer" }}>
                I agree to the Terms of Service and Privacy Policy.
              </Box>
            </Box>

            <Button
              type="submit"
              disabled={!canSubmit}
              fullWidth
              sx={{
                height: 48, borderRadius: "8px", textTransform: "none", fontSize: 15, fontWeight: 700,
                bgcolor: tPrimary, color: "#fff", boxShadow: `0 6px 16px -6px ${alpha(tPrimary, 0.5)}`,
                "&:hover": { bgcolor: tPrimary700, boxShadow: `0 6px 16px -6px ${alpha(tPrimary, 0.5)}` },
                "&.Mui-disabled": { bgcolor: DS.line, color: DS.ink4, boxShadow: "none" },
              }}
            >
              {submitting
                ? "Creating account…"
                : created
                ? mergedExistingAccount
                  ? "Invitation accepted ✓"
                  : "Account created ✓"
                : "Create Account & Accept"}
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: { xs: "14px 22px 22px", sm: "16px 36px 26px" }, textAlign: "center" }}>
          <Box sx={{ fontSize: 12, color: DS.ink4, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <ClockIcon />
            Invitation expires {invite?.expiresAt}
          </Box>
          <Typography sx={{ fontSize: 13, color: DS.ink3, mt: "12px" }}>
            Already have an account?{" "}
            <Box component="span" onClick={() => navigate("/login")} sx={{ color: tPrimary, fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
              Sign in instead
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
